import React, { FC } from 'react';
import { Row, Col, Spin } from 'antd';
import {
  InsuranceSimulationProduct,
  InsuranceUseType,
  Insurance,
} from 'apollo/types';
import { InsuranceCard } from 'components/insurance';
import hdi from 'assets/images/hdi.png';
import chilena from 'assets/images/zurich-logo-blue.svg';
import bci from 'assets/images/bci.png';
import { findIndex } from 'lodash';
import styles from './InsuranceProducts.module.scss';

interface InsuranceProduct extends InsuranceSimulationProduct {
  selected: boolean;
  disabled: boolean;
  show: boolean;
  coverageDetails: string;
}

interface InsuranceProductsProps {
  insuranceProductsOption: InsuranceProduct[];
  setInsuranceProductsOption: React.Dispatch<
    React.SetStateAction<InsuranceProduct[]>
  >;
  proceduresInViewMode: boolean;
  bciSimulationData?:
    | {
        getBciSimulation: InsuranceSimulationProduct[];
      }
    | undefined;
  zurichSimulationData?:
    | {
        getZurichSimulation: InsuranceSimulationProduct[];
      }
    | undefined;
  hdiSimulationData?:
    | {
        getHdiSimulation: InsuranceSimulationProduct[];
      }
    | undefined;
  insuranceData:
    | {
        getInsurance: Insurance;
      }
    | undefined;
  allowedInsurers: string[];
}

const InsuranceProducts: FC<InsuranceProductsProps> = (
  props: InsuranceProductsProps,
) => {
  const {
    insuranceProductsOption,
    setInsuranceProductsOption,
    proceduresInViewMode,
    insuranceData,
    bciSimulationData,
    zurichSimulationData,
    hdiSimulationData,
    allowedInsurers,
  } = props;

  const handleProductSelection = (productId: string) => {
    const index = findIndex(
      insuranceProductsOption,
      (option) => option.productId === productId,
    );

    const newInsuranceOptions = [...insuranceProductsOption];
    newInsuranceOptions[index].selected =
      !insuranceProductsOption[index].selected;
    setInsuranceProductsOption(newInsuranceOptions);
  };

  const renderInsuranceProducts = (insuranceProvider: string) => {
    const insuranceOptions = insuranceProductsOption.filter(
      (product) => product.insuranceProvider === insuranceProvider,
    );

    const titles: Record<string, string> = {
      BCI: 'BCI',
      CHILEAN: 'Zurich',
      HDI: 'HDI',
    };

    const logos: Record<string, string> = {
      BCI: bci,
      CHILEAN: chilena,
      HDI: hdi,
    };

    return (
      insuranceOptions.length > 0 && (
        <>
          <h2 className={styles.InsuranceTitle}>{titles[insuranceProvider]}</h2>
          <hr className={styles.InsuranceSeparator} />
          <Row gutter={[12, 12]} style={{ marginTop: '24px' }}>
            {insuranceProductsOption
              .filter(
                (product) => product.insuranceProvider === insuranceProvider,
              )
              .map(
                (insuranceOption) =>
                  insuranceOption.show && (
                    <Col xs={24} md={6} key={insuranceOption.productId}>
                      <InsuranceCard
                        pdfUrl={insuranceOption.PdfUrl}
                        coverage={insuranceOption.coverageType as string}
                        period={insuranceOption.period}
                        deductible={insuranceOption.deductible}
                        monthlyValue={insuranceOption.monthlyValue}
                        name={insuranceOption.name || ''}
                        selected={
                          proceduresInViewMode ||
                          (insuranceData?.getInsurance
                            .selectedInsuranceProducts &&
                            insuranceData.getInsurance.selectedInsuranceProducts
                              .length > 0)
                            ? true
                            : insuranceOption.selected
                        }
                        details={insuranceOption.coverageDetails}
                        paymentType={insuranceOption.paymentType}
                        monthlyValueInUF={insuranceOption.monthlyValueInUF}
                        installments={insuranceOption.installments}
                        insuranceType={insuranceOption.useType}
                        onClick={
                          proceduresInViewMode
                            ? () => {}
                            : () =>
                                handleProductSelection(
                                  insuranceOption.productId,
                                )
                        }
                        disabled={
                          proceduresInViewMode ||
                          (insuranceData?.getInsurance
                            .selectedInsuranceProducts &&
                            insuranceData.getInsurance.selectedInsuranceProducts
                              .length > 0) ||
                          insuranceOption.disabled ||
                          (!insuranceOption.PdfUrl &&
                            insuranceOption.insuranceProvider !== 'HDI')
                        }
                        insuranceCompany={{
                          name: insuranceProvider,
                          logo: logos[insuranceProvider],
                        }}
                      />
                    </Col>
                  ),
              )}
          </Row>
        </>
      )
    );
  };

  return (
    <>
      <Row
        justify="start"
        className={styles.InsuranceCardsContainer}
        gutter={[24, 24]}
      >
        <>
          {allowedInsurers.includes('CHILEAN') &&
            (insuranceProductsOption.find(
              (option) => option.insuranceProvider === 'CHILEAN',
            ) ? (
              <Col xs={24}>{renderInsuranceProducts('CHILEAN')}</Col>
            ) : (
              insuranceData?.getInsurance?.insuranceInput?.insuranceInterest ===
                'NO HAY INTERES' &&
              zurichSimulationData?.getZurichSimulation?.length !== 0 &&
              !(
                insuranceData?.getInsurance?.selectedInsuranceProducts &&
                insuranceData?.getInsurance?.selectedInsuranceProducts.length >
                  0
              ) && (
                <Col xs={24}>
                  <h2 className={styles.InsuranceTitle}>Zurich</h2>
                  <hr className={styles.InsuranceSeparator} />
                  <Spin style={{ marginTop: '24px' }} />
                </Col>
              )
            ))}

          {allowedInsurers.includes('BCI') &&
            (insuranceProductsOption.find(
              (option) => option.insuranceProvider === 'BCI',
            ) ? (
              <Col xs={24}>{renderInsuranceProducts('BCI')}</Col>
            ) : (
              bciSimulationData?.getBciSimulation?.length !== 0 &&
              !(
                insuranceData?.getInsurance?.selectedInsuranceProducts &&
                insuranceData?.getInsurance?.selectedInsuranceProducts.length >
                  0
              ) && (
                <Col xs={24}>
                  <h2 className={styles.InsuranceTitle}>BCI</h2>
                  <hr className={styles.InsuranceSeparator} />
                  <Spin style={{ marginTop: '24px' }} />
                </Col>
              )
            ))}

          {allowedInsurers.includes('HDI') &&
            (insuranceProductsOption.find(
              (option) => option.insuranceProvider === 'HDI',
            ) ? (
              <Col xs={24}>{renderInsuranceProducts('HDI')}</Col>
            ) : (
              hdiSimulationData?.getHdiSimulation?.length !== 0 &&
              !(
                insuranceData?.getInsurance?.selectedInsuranceProducts &&
                insuranceData?.getInsurance?.selectedInsuranceProducts.length >
                  0
              ) && (
                <Col xs={24}>
                  <h2 className={styles.InsuranceTitle}>HDI</h2>
                  <hr className={styles.InsuranceSeparator} />
                  <Spin style={{ marginTop: '24px' }} />
                </Col>
              )
            ))}

          {allowedInsurers.includes('CHILEAN') &&
            zurichSimulationData &&
            zurichSimulationData.getZurichSimulation?.length === 0 && (
              <Col xs={24}>
                <h2 className={styles.InsuranceTitle}>Zurich</h2>
                <hr className={styles.InsuranceSeparator} />
                <p style={{ marginTop: '24px' }}>
                  No se han encontrado productos en la aseguradora para el
                  modelo y configuraciones seleccionadas
                </p>
              </Col>
            )}

          {allowedInsurers.includes('BCI') &&
            bciSimulationData &&
            bciSimulationData.getBciSimulation?.length === 0 && (
              <Col xs={24}>
                <h2 className={styles.InsuranceTitle}>BCI</h2>
                <hr className={styles.InsuranceSeparator} />
                <p style={{ marginTop: '24px' }}>
                  No se han encontrado productos en la aseguradora para el
                  modelo y configuraciones seleccionadas
                </p>
              </Col>
            )}

          {allowedInsurers.includes('HDI') &&
            hdiSimulationData &&
            hdiSimulationData.getHdiSimulation?.length === 0 && (
              <Col xs={24}>
                <h2 className={styles.InsuranceTitle}>HDI</h2>
                <hr className={styles.InsuranceSeparator} />
                <p style={{ marginTop: '24px' }}>
                  No se han encontrado productos en la aseguradora para el
                  modelo y configuraciones seleccionadas
                </p>
              </Col>
            )}
        </>
      </Row>
    </>
  );
};

InsuranceProducts.defaultProps = {
  bciSimulationData: undefined,
  zurichSimulationData: undefined,
  hdiSimulationData: undefined,
};

export default InsuranceProducts;
